import React, { useState } from 'react';
import { Maximize2, Trash2, GripVertical, FileText, Download, Presentation } from 'lucide-react';

const sizeLabel = (b) => (!b ? '' : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const TYPE_LABEL = { video: 'Video', embed: 'Video', infographic: 'Infographic', document: 'Resource', image: 'Image' };
const TYPE_STYLE = { video: 'bg-blue-500/20 text-blue-300', embed: 'bg-blue-500/20 text-blue-300', infographic: 'bg-green-500/20 text-green-300', document: 'bg-amber-400/20 text-amber-300', image: 'bg-[#1EBFB3]/20 text-[#1EBFB3]' };

const DocumentCard = ({ media, src }) => {
  const ext = (media.filename || '').split('.').pop().toLowerCase();
  const Icon = ['ppt', 'pptx'].includes(ext) ? Presentation : FileText;
  return (
    <div className="flex items-center gap-4 p-5 min-h-[120px]">
      <span className="h-14 w-14 shrink-0 rounded-xl bg-amber-400/10 text-amber-300 flex items-center justify-center"><Icon className="h-7 w-7" /></span>
      <div className="min-w-0 flex-1">
        <p className="text-white font-medium truncate">{media.filename}</p>
        <p className="text-xs text-zinc-500 uppercase">{ext} {sizeLabel(media.size) && `· ${sizeLabel(media.size)}`}</p>
      </div>
      <a href={src} target="_blank" rel="noreferrer" download className="inline-flex items-center gap-1.5 rounded-lg bg-amber-400/15 hover:bg-amber-400/25 text-amber-200 px-3 py-2 text-sm" data-testid={`media-download-${media.id}`}>
        <Download className="h-4 w-4" />Download
      </a>
    </div>
  );
};

const MediaBody = ({ media, src, editMode, onExpand }) => {
  if (media.type === 'document') return <DocumentCard media={media} src={src} />;
  if (media.type === 'embed') return <iframe src={media.embed_url} title={media.caption || media.filename || 'Video'} className="w-full aspect-video" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowFullScreen />;
  if (media.type === 'video') return <video src={src} controls className="w-full aspect-video bg-black" preload="metadata" />;
  return (
    <button type="button" onClick={() => !editMode && onExpand(media)} className={`relative w-full block ${editMode ? '' : 'cursor-zoom-in'}`} disabled={editMode}>
      <img src={src} alt={media.caption || media.filename || 'Section media'} className="w-full aspect-video object-contain bg-zinc-950" />
      {!editMode && <span className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 bg-black/20 transition-opacity"><span className="bg-black/60 rounded-full p-3"><Maximize2 className="h-6 w-6 text-white" /></span></span>}
    </button>
  );
};

export default function EmbeddedMediaGrid({ media, editMode, resolveUrl, onChange, onExpand }) {
  const [dragId, setDragId] = useState(null);
  const update = (id, patch) => onChange(media.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  const drop = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const next = [...media];
    const from = next.findIndex((m) => m.id === dragId);
    const to = next.findIndex((m) => m.id === targetId);
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
    setDragId(null);
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="embedded-media-grid">
      {media.map((m) => (
        <div key={m.id} draggable={editMode} onDragStart={() => setDragId(m.id)} onDragOver={(e) => editMode && e.preventDefault()} onDrop={() => drop(m.id)} onDragEnd={() => setDragId(null)}
          className={`relative bg-zinc-800/50 border rounded-xl overflow-hidden group transition-colors ${dragId === m.id ? 'opacity-50 border-teal-400' : 'border-zinc-700'} ${m.type === 'document' ? 'md:col-span-2' : ''}`}
          data-testid={`embedded-media-${m.id}`}>
          <MediaBody media={m} src={resolveUrl(m.url)} editMode={editMode} onExpand={onExpand} />
          {editMode ? (
            <div className="p-3 flex gap-2">
              <input type="text" value={m.caption || ''} onChange={(e) => update(m.id, { caption: e.target.value })} placeholder="Add caption..."
                className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-300 placeholder-zinc-500 focus:border-[#1EBFB3]" data-testid={`media-caption-${m.id}`} />
              {['image', 'infographic'].includes(m.type) && (
                <select value={m.type} onChange={(e) => update(m.id, { type: e.target.value })} className="bg-zinc-900 border border-zinc-700 rounded-lg px-2 text-xs text-zinc-300" data-testid={`media-kind-${m.id}`}>
                  <option value="image">Image</option><option value="infographic">Infographic</option>
                </select>
              )}
            </div>
          ) : m.caption ? <p className="p-3 text-sm text-zinc-400 italic">{m.caption}</p> : null}
          {editMode && (
            <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <span className="p-1.5 bg-zinc-900/80 text-zinc-400 rounded cursor-grab" title="Drag to reorder"><GripVertical className="h-4 w-4" /></span>
              <button type="button" onClick={() => onChange(media.filter((x) => x.id !== m.id))} className="p-1.5 bg-zinc-900/80 text-zinc-400 hover:text-red-400 rounded" title="Remove media" data-testid={`media-remove-${m.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          )}
          <span className={`absolute top-2 left-2 text-xs px-2 py-1 rounded-full ${TYPE_STYLE[m.type] || TYPE_STYLE.image}`}>{TYPE_LABEL[m.type] || 'Image'}</span>
        </div>
      ))}
    </div>
  );
}
